import { useEffect, useMemo, useRef } from 'react';
import { BackHandler, Platform } from 'react-native';
import {
  dispatchRegisteredBack,
  hasRegisteredBackHandlers,
  registerBackHandler,
  subscribeBackRegistry,
  updateBackHandler,
} from './backRegistry';

type UseBackHandlerOptions = {
  id: string;
  label?: string;
  priority?: number;
  enabled?: boolean;
  onBack: () => boolean;
};

let nativeInstalled = false;
let webInstalled = false;
let webGuardActive = false;
let webGuardInHistory = false;
let webSuppressNextPop = false;
let webGuardUrl = '';

function currentHistoryHasGuard() {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
  return !!window.history.state?.__fishMemoryBackGuard;
}

function installNativeBackHandler() {
  if (nativeInstalled || Platform.OS === 'web') return;
  nativeInstalled = true;
  BackHandler.addEventListener('hardwareBackPress', () => dispatchRegisteredBack());
}

function pushWebGuard() {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  const href = window.location.href;
  if (webGuardActive && currentHistoryHasGuard() && webGuardUrl === href) return;
  try {
    window.history.pushState(
      { ...(window.history.state || {}), __fishMemoryBackGuard: true },
      '',
      href,
    );
    webGuardActive = true;
    webGuardInHistory = true;
    webGuardUrl = href;
  } catch {
    webGuardActive = false;
  }
}

function syncWebGuard() {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  if (hasRegisteredBackHandlers()) {
    pushWebGuard();
    return;
  }
  webGuardActive = currentHistoryHasGuard();
  if (!webGuardActive) webGuardUrl = '';
}

function installWebBackHandler() {
  if (webInstalled || Platform.OS !== 'web' || typeof window === 'undefined') return;
  webInstalled = true;

  window.addEventListener('popstate', () => {
    if (webSuppressNextPop) {
      webSuppressNextPop = false;
      return;
    }

    const wasGuardInHistory = webGuardInHistory;
    const handled = dispatchRegisteredBack();
    webGuardActive = false;
    webGuardInHistory = false;
    webGuardUrl = '';
    if (handled && hasRegisteredBackHandlers()) {
      window.setTimeout(pushWebGuard, 0);
      return;
    }

    // If this pop only removed our guard and there is no app-layer state left,
    // continue to the real previous entry so the user does not need two backs.
    if (wasGuardInHistory && !handled && !hasRegisteredBackHandlers()) {
      webSuppressNextPop = true;
      window.setTimeout(() => {
        try { window.history.back(); } catch { webSuppressNextPop = false; }
      }, 0);
    }
  }, { capture: true });

  subscribeBackRegistry(syncWebGuard);
  syncWebGuard();
}

/**
 * Programmatically leave a guarded screen (e.g. an in-app "Back" button).
 *
 * On web our back-guard pushes an extra same-URL history entry to trap the
 * browser back button. A plain router.back() (history.go -1) only pops THAT
 * guard entry, leaving the user on the same screen — the "stuck back button"
 * bug. When the guard is present we skip both the guard AND the screen entry
 * (go -2) and let React Navigation reconcile from the resulting URL; the
 * guard's own popstate handler is suppressed so it doesn't fight the nav.
 * Native (no guard): just runs the fallback (router.back()).
 */
export function leaveGuardedScreen(fallback: () => void) {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    fallback();
    return;
  }
  // Our guard added one extra same-URL history entry on top of the screen.
  // Step 1: pop just the guard entry (so the browser history once again
  // matches what React Navigation thinks it owns). Step 2, once that pop has
  // landed, run the REAL back (router.back) which is React Navigation's own
  // navigation and pops the screen cleanly. Doing router.back() before
  // removing the guard would only pop the guard and leave us stuck; using
  // history.go(-2) makes RN snap the URL back. This two-step avoids both.
  if (currentHistoryHasGuard()) {
    try {
      webSuppressNextPop = true; // our guard popstate handler must not re-fire
      webGuardActive = false;
      webGuardInHistory = false;
      webGuardUrl = '';
      const afterGuardPop = () => {
        window.removeEventListener('popstate', afterGuardPop, true);
        fallback();
      };
      window.addEventListener('popstate', afterGuardPop, true);
      window.history.back();
      return;
    } catch {
      webSuppressNextPop = false;
    }
  }
  fallback();
}

export function useBackHandler({ id, label, priority = 0, enabled = true, onBack }: UseBackHandlerOptions) {
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  const entry = useMemo(() => ({
    id,
    label,
    priority,
    enabled,
    handleBack: () => onBackRef.current(),
  }), [enabled, id, label, priority]);

  useEffect(() => {
    installNativeBackHandler();
    installWebBackHandler();
  }, []);

  useEffect(() => {
    const unregister = registerBackHandler(entry);
    if (Platform.OS === 'web' && entry.enabled) {
      webGuardActive = false;
      syncWebGuard();
      window.setTimeout(() => {
        webGuardActive = false;
        syncWebGuard();
      }, 80);
    }
    return unregister;
    // Register only once by id; subsequent entry changes are applied below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    updateBackHandler(entry);
    if (Platform.OS === 'web') syncWebGuard();
    if (Platform.OS === 'web' && entry.enabled) {
      window.setTimeout(() => {
        webGuardActive = false;
        syncWebGuard();
      }, 80);
    }
  }, [entry]);
}
